import React, { useCallback, useEffect, useState } from "react";
import PageWithSidebar from "components/PageWithSidebar";
import EndpointSecurityMenu from "../components/EndpointSecurityMenu";
import { store } from "services/state";
import { updateEndpointACLApisList } from "../services/api-catalog";
import EndpointSecurityTable from "components/EndpointSecurityTable";
import { RestApiColumns, WebsocketApiColumns } from "components/Columns";
import Sidebar from "components/Sidebar/Sidebar";
import { Link, useLocation } from "react-router";
import _ from "lodash";

import { isRestAPIUser, isWsAPIUser } from "services/self";
import {
  Container,
  Header,
  Image,
  List,
  Menu,
  Search,
  Icon,
  Dropdown,
  Accordion,
} from "semantic-ui-react";

import SidebarHeader from "components/Sidebar/SidebarHeader";
import { convertOptions } from "utils/utility";

export const REST_API = "Rest APIs";
export const WEBSOCKET_API = "Websocket APIs";
const API_DETAILS_PATH_INDEX = 4;

function EndpointSecurity() {
  const [endpointACLList, setEndpointACLList] = useState(
    store.endpointACLApiList
  );
  const [loaded, setLoaded] = useState(false);
  const [columns, setColumns] = useState();
  const [api, setApi] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({});

  const filterOptions = {
    Domain: ["Public", "Private"],
    IPWhitelisted: ["Yes", "No"],
  };

  const isApiUrl = useLocation().pathname.split("/")?.[API_DETAILS_PATH_INDEX];

  const filterDomain = (api, input) =>
    (input === "Private") === api.api_endpoint.includes("priv");

  const filterIpWhitelisted = (api, input) =>
    (input === "Yes") === api.isIPWhitelisted;

  useEffect(() => {
    async function fetchData() {
      await updateEndpointACLApisList(true);
      if (store.endpointACLApiList.loaded) {
        setEndpointACLList(store.endpointACLApiList);
        if (isRestAPIUser()) {
          setColumns(RestApiColumns);
        } else if (isWsAPIUser()) {
          setColumns(WebsocketApiColumns);
        }
        setLoaded(true);
      }
    }
    fetchData();
  }, []);

  const filterCurityScopes = useCallback(
    (api) => {
      return Object.entries(filter)
        .filter(([key, value]) => value !== "")
        .every(([key, value]) => {
          switch (key) {
            case "Domain":
              return filterDomain(api, value);
            case "IPWhitelisted":
              return filterIpWhitelisted(api, value);
            default:
              return true;
          }
        });
    },
    [filter]
  );

  const filterEndpointAclList = useCallback(
    (apis) => {
      const filteredApiList = apis.map(({ title, content }) => ({
        title: title,
        content: content
          .filter(({ curityScopes }) => curityScopes.length > 0)
          .filter(({ curityScopes }) =>
            curityScopes.some((api) => filterCurityScopes(api))
          ),
      }));

      return filteredApiList.filter(({ content }) => content.length > 0);
    },
    [filterCurityScopes]
  );

  const setFilteredList = useCallback(() => {
    setEndpointACLList({
      restApis: filterEndpointAclList(store.endpointACLApiList.restApis),
      websocketApis: filterEndpointAclList(
        store.endpointACLApiList.websocketApis
      ),
    });
  }, [filterEndpointAclList]);

  useEffect(() => {
    setFilteredList();
  }, [filter, setFilteredList]);

  const displayTable = (api, apiType) => {
    setApi(api);
    // handle both search and manual click
    if (apiType === "restApis") apiType = REST_API;
    else if (apiType === "websocketApis") apiType = WEBSOCKET_API;
    if (apiType === REST_API) setColumns(RestApiColumns);
    else if (apiType === WEBSOCKET_API) setColumns(WebsocketApiColumns);
  };

  const handleSearchChange = (e, { value }) => {
    setSearchTerm(value);
    const filteredData = Object.entries(endpointACLList).flatMap(
      ([categoryName, categoryItems]) => {
        if (categoryName === "restApis" || categoryName === "websocketApis") {
          const apiType = categoryName === "restApis" ? "rest" : "websocket";
          return categoryItems.flatMap((module) => {
            return module.content
              .filter((item) =>
                item.name.toLowerCase().includes(value.toLowerCase())
              )
              .map((item) => ({
                ...item,
                categoryName: categoryName,
                path: apiType + "/" + module.title + "/" + item.name,
                location: apiType + " > " + module.title,
              }));
          });
        }
        return [];
      }
    );
    setFilteredResults(filteredData);
  };

  const handleSelect = (e, { result }) => {
    try {
      if (result) {
        if (displayTable) displayTable(result, result.categoryName);
      }
    } catch (error) {}
  };

  const resultRenderer = (result) => {
    return (
      <Menu.Menu
        as={Link}
        to={`/endpoint-security/${result.path}`}
        style={{
          display: "inline-block",
          width: "100%",
          height: "100%",
        }}
      >
        {result.location} <br />
        {result.name}
      </Menu.Menu>
    );
  };

  const sidebar = (
    <Sidebar>
      <SidebarHeader
        className="item"
        style={{ fontWeight: "400", fontSize: "1em", cursor: "pointer" }}
      >
        Endpoint Security
      </SidebarHeader>

      <Search
        style={{ margin: "1em" }}
        placeholder="Search API"
        onSearchChange={_.debounce(handleSearchChange, 500, {
          leading: true,
        })}
        onResultSelect={(e, { result }) => handleSelect(e, { result })}
        resultRenderer={resultRenderer}
        results={filteredResults}
        value={searchTerm}
      />
      <Accordion
        styled
        style={{ width: "80%", height: "50%", marginLeft: "1em" }}
        onClick={() => setShowFilter((state) => !state)}
      >
        <Accordion.Title>
          Filter APIs
          <Icon
            name={`${showFilter ? "angle up" : "angle down"}`}
            floated="right"
          />
          {Object.entries(filter).length > 0
            ? `(${Object.entries(filter).length})`
            : ""}
          {Object.entries(filter).length > 0 && (
            <Icon
              name="cancel"
              title="Clear filter(s)"
              onClick={() => {
                setFilter({});
              }}
            />
          )}
        </Accordion.Title>
        {Object.entries(filterOptions).map(([key, value]) => (
          <Accordion.Content
            key={key}
            active={showFilter}
            style={{ width: "90%", marginLeft: "0" }}
          >
            {key}:
            <Dropdown
              label={key}
              style={{ minWidth: "7em", marginLeft: "0" }}
              placeholder="Select"
              clearable
              value={filter[key] || ""}
              options={convertOptions(value)}
              onChange={(e, { value }) => {
                value === ""
                  ? setFilter((filter) => _.omit(filter, key))
                  : setFilter({ ...filter, [key]: value });
              }}
            ></Dropdown>
          </Accordion.Content>
        ))}
      </Accordion>

      {loaded && (
        <EndpointSecurityMenu
          apiList={endpointACLList}
          showTable={displayTable}
        />
      )}
    </Sidebar>
  );

  return (
    <PageWithSidebar
      sidebarContent={sidebar}
      SidebarPusherProps={{ className: "swagger-section" }}
    >
      {api ? (
        <EndpointSecurityTable columns={columns} api={api} />
      ) : (
        !isApiUrl && (
          <Container fluid style={{ padding: "2em" }}>
            <Header as="h2">Overview</Header>
            <p>
              Securing API access is crucial for protecting data and ensuring
              reliable interactions with our services. API Gateway system is
              engineered to provide robust authentication, safeguarding Client
              transactions while maintaining seamless connectivity. Here's a
              brief overview of our authentication process:{" "}
            </p>
            <List bulleted relaxed>
              <List.Item>
                <List.Header as="h4">Split-Token Approach: </List.Header>
                <List.Description>
                  We leverage a split-token method, where the Client send a JWT
                  signature as a bearer token. This strategy enable security by
                  separating token responsibilities, minimizing exposure and
                  risks.
                </List.Description>
              </List.Item>

              <List.Item>
                <List.Header as="h4">Efficient Token Management: </List.Header>
                <List.Description>
                  Upon Client request, our API Gateway collaborates with Curity
                  to assemble the complete JWT token. This token is then
                  securely relayed to our backend systems, ensuring that your
                  access is authenticated promptly and securely.
                </List.Description>
              </List.Item>

              <List.Item>
                <List.Header as="h4">Accessible Token Retrieval: </List.Header>
                <List.Description>
                  Acquire your JWT token easily through our dedicated token
                  example URI:{" "}
                  <i>
                    https://dev-org-curity.sec.bank.ikano/oauth/v2/oauth-token
                  </i>
                  . This endpoint is your reliable source for secure
                  authentication tokens.
                </List.Description>
              </List.Item>

              <List.Item>
                <List.Header as="h4">API Types: </List.Header>
                <List.Description>
                  To accommodate various application needs, we offer both REST
                  and WebSocket APIs. To enable flexible integration, aligning
                  with different technical requirements and preferences.
                </List.Description>
              </List.Item>

              <List.Item>
                <List.Header as="h4">Token Placement Guidelines: </List.Header>
                <List bulleted>
                  <List.Item>Authorization header for REST API</List.Item>
                  <List.Item>
                    Query params for websocket API (auth-token and action ){" "}
                  </List.Item>
                </List>
              </List.Item>
            </List>
            <Header as="h3">Authentication Flow</Header>
            <p>Below you can find a diagram of authentication activity flow</p>
            <br></br>
            <Image
              src="/custom-content/endpoint-security-flow.png"
              centered
            ></Image>

            <Header as="h4">Authentication flow steps:</Header>
            <List ordered>
              <List.Item>
                Mobile/Web app (consumers) will get JWT signature token from
                token service using proper client and scopes details.
              </List.Item>
              <List.Item>
                Consumers will receive a token and make API call
              </List.Item>
              <List.Item>
                Send received token with request to API Gateway(API GW) which
                will be fronted by WAF firewall for protecting from security
                threats.
              </List.Item>
              <List.Item>
                AP GW will perform AuthN/AuthZ with Curity and checks throttling
                policies before processing request.
              </List.Item>
              <List.Item>
                API GW sends transformed request with complete JWT token to
                backend modules via network load balancer.
              </List.Item>
              <List.Item>
                Microservices will push response back to API GW using connection
                URL for asynchronous calls
              </List.Item>
            </List>
          </Container>
        )
      )}
    </PageWithSidebar>
  );
}

export default EndpointSecurity;
